import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type ConfirmConsentResult } from '@whinerp/client';
import { Button, PortalShell } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { portalShellLabels } from './portal-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-62 Confirm a subscription (F-082).
 *
 * The link in the email opens this page, and the page does NOT confirm on
 * load: mail scanners open every link in a message, and a confirmation a
 * scanner can give is not one a person gave. The person presses the button,
 * having been shown what they are agreeing to.
 */
function ConfirmConsentScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const token = useParams()['token'] ?? '';
  const confirm = useMutation<ConfirmConsentResult, ApiError>({
    mutationFn: () => api.confirmConsent({ body: { token } }),
  });
  const outcome = confirm.data?.outcome ?? null;

  return (
    <PortalShell surface="consent" workspaceName={null} refusal={null} labels={portalShellLabels(t, null)}>
      <section className="flex flex-col gap-3">
        <h1 className="text-lead text-ink">{t('confirm.title')}</h1>
        {confirm.error ? <p data-confirm-refusal className="text-dense text-danger-text">{errorText(confirm.error)}</p> : null}
        {outcome === null ? (
          <div>
            <Button intent="primary" data-confirm-consent disabled={confirm.isPending} onClick={() => confirm.mutate()}>
              {t('confirm.title')}
            </Button>
          </div>
        ) : (
          <p data-confirm-outcome={outcome} className="text-body text-ink">
            {t(
              outcome === 'confirmed'
                ? 'confirm.confirmed'
                : outcome === 'already-confirmed'
                  ? 'confirm.already'
                  : outcome === 'expired'
                    ? 'confirm.expired'
                    : 'confirm.invalid',
            )}
          </p>
        )}
        {confirm.data?.wording ? (
          <blockquote data-confirm-wording className="border-s-2 border-s-rule ps-3 text-dense text-ink">
            <span className="block text-meta text-ink-meta">{t('confirm.agreed')}</span>
            {confirm.data.wording}
          </blockquote>
        ) : null}
      </section>
    </PortalShell>
  );
}

function ConfirmLoading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function ConfirmError(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-danger-text">{t('confirm.invalid')}</p>;
}
function ConfirmEmpty(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-ink-meta">{t('confirm.invalid')}</p>;
}

export const screen = defineScreen({
  id: 'SC-62',
  route: '/confirm/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Confirm your subscription',
  features: ['F-082'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: ['PortalShell'],
  elements: ['PortalShell'],
  sampleParams: { token: 'sample-not-a-token' },
  states: { empty: ConfirmEmpty, loading: ConfirmLoading, error: ConfirmError },
  component: ConfirmConsentScreen,
});

export default screen;
